//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import {
  ContentLibrarySortType,
  IContentAggregateMediaStats,
  IContentLibraryFiltersOptions,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { ItemsSummary } from '@media-library/components/top-bar/ItemsSummary';
import { MediaLibraryMetricsSummary } from '@media-library/components/top-bar/MediaLibraryMetricsSummary';
import { MediaLibrarySearchText } from '@media-library/components/top-bar/MediaLibrarySearchText';
import { MediaLibrarySortDropdown } from '@media-library/components/top-bar/MediaLibrarySortDropdown';
import { ShowFilterPanelButton } from '@media-library/components/top-bar/ShowFilterPanelButton';
import { MediaLibrarySourcesDropdown } from '@media-library/components/top-bar/sources-dropdown/MediaLibrarySourcesDropdown';
import { MediaLibrarySortType } from '@media-library/constants/mediaLibraryConstants';
import {
  useGetInstagramConnectedAccountsQuery,
  useGetInstagramPrimaryAccountQuery,
  useGetInstagramTrackedCompetitorsQuery,
  useGetInstagramTrackedUsersAndHashtagsQuery,
  useGetPinterestUserQuery,
  useGetTeamMembersQuery,
  useGetTikTokConnectionStatusQuery,
} from '@media-library/queries';
import { getClientId } from '@redux/selectors/getClientId';
import { getClientName } from '@redux/selectors/getSessionInfo';

interface MediaLibraryContentTopBarProps {
  filters?: IContentLibraryFiltersOptions;
  handleChangeSearchText: (searchText: string) => void;
  handleChangeSortType: (sortType: MediaLibrarySortType) => void;
  handleChangeSources: (sources: IContentLibrarySourcesOptions) => void;
  handleToggleFilterPanel: () => void;
  initialSearchText: string;
  isLoading: boolean;
  itemCount: number;
  itemStats?: IContentAggregateMediaStats;
  orderBy?: ContentLibrarySortType;
  showSummary?: boolean;
  showSummaryToggle?: ReactNode;
  sortType: MediaLibrarySortType;
  sources: IContentLibrarySourcesOptions;
}

export const MediaLibraryContentTopBar = ({
  filters,
  handleChangeSearchText,
  handleChangeSortType,
  handleChangeSources,
  handleToggleFilterPanel,
  initialSearchText,
  isLoading,
  itemCount,
  itemStats,
  orderBy,
  showSummary,
  showSummaryToggle,
  sortType,
  sources,
}: MediaLibraryContentTopBarProps) => {
  const { state } = useCurrentStateAndParams();

  const clientId = useSelector(getClientId);
  const clientName = useSelector(getClientName);

  const [searchText, setSearchText] = useState<string>(initialSearchText);

  const debounceSetSearchText = useDebouncedCallback((searchText: string) => {
    handleChangeSearchText(searchText);
  }, 300);

  const { data: teamMembers = [], isLoading: isLoadingTeamMembers } =
    useGetTeamMembersQuery(clientId, clientName);

  const {
    data: defaultInstagramAccount,
    isLoading: isLoadingInstagramPrimaryAccount,
  } = useGetInstagramPrimaryAccountQuery();

  const {
    data: competitors = [],
    isLoading: isLoadingInstagramTrackedCompetitors,
  } = useGetInstagramTrackedCompetitorsQuery();

  const {
    data: instagramTrackedUsersAndHashtags,
    isLoading: isLoadingInstagramTrackedUsersAndHashtags,
  } = useGetInstagramTrackedUsersAndHashtagsQuery();
  const {
    businessUsers = [],
    hashtags = [],
    users: regularUsers = [],
  } = instagramTrackedUsersAndHashtags ?? {};

  const { data: pinterestUser, isLoading: isLoadingPinterestUser } =
    useGetPinterestUserQuery();

  const { data: defaultTikTokAccount, isLoading: isLoadingTikTokUser } =
    useGetTikTokConnectionStatusQuery();

  const {
    data: connectedInstagramAccounts,
    isLoading: isLoadingInstagramMultiHandle,
  } = useGetInstagramConnectedAccountsQuery();

  const isLoadingSourcesDropdownData =
    isLoadingTeamMembers ||
    isLoadingInstagramPrimaryAccount ||
    isLoadingInstagramTrackedCompetitors ||
    isLoadingInstagramTrackedUsersAndHashtags ||
    isLoadingInstagramMultiHandle ||
    isLoadingPinterestUser ||
    isLoadingTikTokUser;

  useEffect(() => {
    // Cleanup function. Clear search text when navigate to other pages.
    return () => {
      setSearchText('');
    };
  }, [state]);

  return (
    <>
      <MediaLibraryTopBarContainer>
        <MediaLibraryTopBarWrapper>
          <MediaLibrarySourcesDropdown
            handleChangeSources={handleChangeSources}
            instagramConfigs={{
              connectedInstagramAccounts: connectedInstagramAccounts,
              defaultInstagramAccount,
              trackedBusinessUsers: businessUsers,
              trackedCompetitors: competitors,
              trackedHashtags: hashtags,
              trackedUsers: regularUsers,
            }}
            isDisabled={isLoadingSourcesDropdownData}
            pinterestUser={pinterestUser}
            selectedSources={sources}
            teamMembers={teamMembers}
            tiktokConfigs={{ defaultTikTokAccount }}
          />

          <ItemsSummary
            isLoading={isLoading}
            itemCount={itemCount}
            showSummaryToggle={showSummaryToggle}
          />
        </MediaLibraryTopBarWrapper>

        <MediaLibraryTopBarWrapper>
          <MediaLibrarySearchText
            handleChangeSearchText={(newSearchText: string) => {
              setSearchText(newSearchText);
              debounceSetSearchText(newSearchText);
            }}
            searchText={searchText}
          />

          <MediaLibrarySortDropdown
            handleChangeSortType={handleChangeSortType}
            sortType={sortType}
          />

          <ShowFilterPanelButton onClick={handleToggleFilterPanel} />
        </MediaLibraryTopBarWrapper>
      </MediaLibraryTopBarContainer>
      {showSummary && (
        <MediaLibraryMetricsSummary
          filters={filters}
          isLoading={isLoading}
          itemCount={itemCount}
          itemStats={itemStats}
          orderBy={orderBy}
          sources={sources}
        />
      )}
    </>
  );
};

const MediaLibraryTopBarContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacings[3]};
`,
);

const MediaLibraryTopBarWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacings[3]};
`,
);
